import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from '@freighthub/silhouette-core'
import { CurrencyCode } from 'features/BookingFlow'
import { ChangeEvent, FC, useState } from 'react'
import { useUIStore } from 'service/useUIStore'
import { useI18n } from 'utils/localisation'

import { useLocalCurrency } from './useLocalCurrency'

export const translationKeys = {
  title: 'display_currency_dialog-title',
  subtitle: 'display_currency_dialog-subtitle',
  info: 'display_currency_dialog-info',
  cancel: 'display_currency_dialog-action_cancel',
  save: 'display_currency_dialog-action_save',
  notificationSuccess: 'display_currency_dialog-notification_success',
}

type LocalCurrencySelectorDialogProps = {
  closeDialog: () => void
}

const LocalCurrencySelectorDialog: FC<LocalCurrencySelectorDialogProps> = ({ closeDialog }) => {
  const { localCurrency, storeLocalCurrency } = useLocalCurrency()
  const [value, setValue] = useState(localCurrency)
  const { translate } = useI18n()
  const { snack } = useUIStore()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value as CurrencyCode)
  }

  const save = () => {
    storeLocalCurrency(value)
    closeDialog()
    snack.notifySuccess(translate(translationKeys.notificationSuccess, { currencyCode: value }))
  }

  return (
    <Dialog open onClose={closeDialog} maxWidth="sm">
      <DialogTitle>{translate(translationKeys.title)}</DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1">{translate(translationKeys.subtitle)}</Typography>
        <Typography>{translate(translationKeys.info)}</Typography>

        <FormControl>
          <RadioGroup value={value} onChange={handleChange}>
            <FormControlLabel value="EUR" control={<Radio color="primary" />} label="Euro" />
            <FormControlLabel value="USD" control={<Radio color="primary" />} label="US Dollars" />
            <FormControlLabel value="DKK" control={<Radio color="primary" />} label="Danish krone" />
            <FormControlLabel value="VND" control={<Radio color="primary" />} label="Vietnamese dong" />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeDialog}>{translate(translationKeys.cancel)}</Button>
        <Button variant="contained" onClick={save}>
          {translate(translationKeys.save)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  button: {
    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    },
  },
}))

export const LocalCurrencySelectorFlow: FC = () => {
  const classes = useStyles()
  const { localCurrency } = useLocalCurrency()

  const [dialogVisible, setDialogVisible] = useState(false)

  const openDialog = () => {
    setDialogVisible(true)
  }

  const closeDialog = () => {
    setDialogVisible(false)
  }

  return (
    <>
      <Button onClick={openDialog} className={classes.button} color="inherit">
        {localCurrency}
      </Button>

      {dialogVisible && <LocalCurrencySelectorDialog closeDialog={closeDialog} />}
    </>
  )
}
